import { LuSun, LuMoon } from "react-icons/lu";
import { useTheme, THEME_OPTIONS, LIGHT_BG, LIGHT_TEXT, DARK_BG, DARK_TEXT, type ColorMode } from "../state/themeState.js";
import { useViewSettings, FONT_SIZE_VALUES, FONT_WEIGHT_VALUES } from "../state/viewSettingsState.js";
import styles from "../styles/components/ViewSettings.module.css";

const COLOR_MODES: { mode: ColorMode; icon: React.ReactNode; label: string; bg: string; text: string }[] = [
  { mode: "light", icon: <LuSun size={14} />,  label: "Light", bg: LIGHT_BG, text: LIGHT_TEXT },
  { mode: "dark",  icon: <LuMoon size={14} />, label: "Dark",  bg: DARK_BG,  text: DARK_TEXT },
];

export function ViewSettings({ mobile = false }: { mobile?: boolean }) {
  const { themeIndex, isDark, colorMode, setThemeIndex, setHoveredThemeIndex, setColorMode, resetToDefaults: resetTheme } = useTheme();
  const { hideRead, columnCount, grouping, fontSize, fontWeight, setHideRead, setColumnCount, setGrouping, setFontSize, setFontWeight, resetToDefaults: resetView } = useViewSettings();

  function handleReset() {
    resetTheme();
    resetView();
  }

  return (
    <div className={styles.widget} data-mobile={mobile || undefined}>
      <div className={styles.section}>
        <span className={styles.label}>Mode</span>
        <div className={styles.modeSwatchGrid}>
          {COLOR_MODES.map(({ mode, icon, label, bg, text }) => (
            <button
              key={mode}
              type="button"
              title={label}
              className={`${styles.modeSwatch} ${colorMode === mode ? styles.modeSwatchActive : ""}`}
              style={{ "--mode-bg": bg, "--mode-text": text } as React.CSSProperties}
              onClick={() => setColorMode(mode)}
            >
              {icon}
              <span className={styles.modeSwatchLabel}>{label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className={styles.section}>
        <span className={styles.label}>Theme</span>
      </div>
      <div className={styles.swatchGrid} onMouseLeave={() => setHoveredThemeIndex(null)}>
        {THEME_OPTIONS.map(({ preset, i }) => (
          <button
            key={i}
            type="button"
            title={preset.name}
            className={`${styles.swatchBtn} ${i === themeIndex ? styles.swatchBtnActive : ""}`}
            style={{
              "--swatch-primary": preset[isDark ? "dark" : "light"].primary,
              "--swatch-secondary": preset[isDark ? "dark" : "light"].secondary,
            } as React.CSSProperties}
            onMouseEnter={() => setHoveredThemeIndex(i)}
            onClick={() => { setThemeIndex(i); }}
          />
        ))}

      </div>

      <div className={styles.section}>
        <span className={styles.label}>Grouping</span>
        <div className={styles.alignButtons}>
          {(["source", "none"] as const).map((v) => (
            <button
              key={v}
              type="button"
              className={`${styles.alignBtn} ${grouping === v ? styles.alignBtnActive : ""}`}
              onClick={() => setGrouping(v)}
            >
              {v === "source" ? "By Source" : "None"}
            </button>
          ))}
        </div>
      </div>

      <div className={styles.section}>
        <span className={styles.label}>Size</span>
        <div className={styles.alignButtons}>
          {(["md", "lg", "xl"] as const).map((v) => (
            <button
              key={v}
              type="button"
              className={`${styles.alignBtn} ${fontSize === v ? styles.alignBtnActive : ""}`}
              style={{ fontSize: FONT_SIZE_VALUES[v] }}
              onClick={() => setFontSize(v)}
            >
              Aa
            </button>
          ))}
        </div>
      </div>

      <div className={styles.section}>
        <span className={styles.label}>Weight</span>
        <div className={styles.alignButtons}>
          {(["regular", "medium"] as const).map((v) => (
            <button
              key={v}
              type="button"
              className={`${styles.alignBtn} ${fontWeight === v ? styles.alignBtnActive : ""}`}
              style={{ fontWeight: FONT_WEIGHT_VALUES[v] }}
              onClick={() => setFontWeight(v)}
            >
              {v === "regular" ? "Regular" : "Medium"}
            </button>
          ))}
        </div>
      </div>

      <div className={styles.section}>
        <span className={styles.label}>Hide read</span>
        <button
          type="button"
          className={`${styles.toggleBtn} ${hideRead ? styles.toggleBtnActive : ""}`}
          onClick={() => setHideRead(!hideRead)}
        >
          {hideRead ? "On" : "Off"}
        </button>
      </div>

      <div className={`${styles.section} ${styles.columnsSection}`}>
        <span className={styles.label}>Columns</span>
        <button
          type="button"
          className={styles.dotPicker}
          title={`${columnCount} column${columnCount > 1 ? "s" : ""} — click to cycle`}
          onClick={() => setColumnCount(columnCount >= 4 ? 1 : columnCount + 1)}
        >
          {[1, 2, 3, 4].map((n) => (
            <span
              key={n}
              className={`${styles.dot} ${n <= columnCount ? styles.dotFilled : ""}`}
            />
          ))}
        </button>
      </div>

      <div className={styles.resetRow}>
        <button type="button" className={styles.resetBtn} onClick={handleReset}>
          Reset to Defaults
        </button>
      </div>
    </div>
  );
}
